import React, { useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react"

import { constructorContext } from "../Settings/Builder"

import {
	Alert,
	AlertTitle,
  Box,
  CircularProgress,
  Grid,
  Typography,
  TextField,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Divider,
  Chip,
  Paper,
  InputBase,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material"

import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Send as SendIcon,
  Edit as EditIcon,
  ContentCopy as ContentCopyIcon,
  AccountCircle as AccountCircleIcon,
  Info as InfoIcon,
  LibraryBooks as LibraryBooksIcon,
  Palette as PaletteIcon,
  Chat as ChatIcon,
  Assignment as AssignmentIcon,
  Psychology as PsychologyIcon,
  Circle as CircleIcon
} from "@mui/icons-material"

import { DataGridPro, GridToolbarContainer, ptBR } from "@mui/x-data-grid-pro"
import api from "../../api"
import { getSession } from "../../auth"
import { lockedReducer } from "../../utils/defaultReducer"
import Loading from "../Loading"
import dayjs from 'dayjs'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import { ptBR as corePtBR } from '@mui/material/locale'

import ConfirmationDialogCaptcha from '../Dialogs/ConfimationDialogCaptcha'
import useNotification from "../Notification"

import './styles.css'
import ConfirmationDialog from "../Dialogs/Confirmation"

import SupportAgentIcon from "@mui/icons-material/SupportAgent"
import ContentUnavailable from "../ContentUnavailable"
import { useMenu } from "../../contexts/MenuContext"

const theme = createTheme({
	palette: {
		primary: { main: '#21446C' },
	},
}, ptBR, corePtBR)

const limits = {
	name                  : 64,
	persona_identity      : 4096,
	description           : 1024,
	persona_style         : 4096,
	persona_response      : 4096,
	persona_tasks         : 4096,
	intentions            : 5,
	intention_description : 128
}

const functions = {
	"#FINALIZAR_ATENDIMENTO" : "Finalizar Atendimento",
	"#TRANSFERIR_BOT"        : "Transferir para ChatBot",   
	"#TRANSFERIR_FILA"       : "Transferir para Fila",
	"#TRANSFERIR_AGENTE"     : "Transferir para Agente de IA",
}

const agentInitialIntention = {
	required: true, 
	type: "#FINALIZAR_ATENDIMENTO", 
	description: "Quando quiser finalizar o atendimento.", 
	message: "Claro! Vou finalizar o atendimento."
}

const allowedExtensions = [".pdf", ".txt", ".doc", ".docx", ".md", ".xlsx", ".xls"]

const AgentList = ({ parent, setParent, loadAgents }) => {

	const [ agentToDelete, setAgentToDelete ] = useState(null)
	const [confirmDuplicate, setConfirmDuplicate] = useState(null)
	const [ upload, setUpload ] = useState(false)
  const [ file, setFile ] = useState(null)

	const createAgent = useCallback(async () => {

		setParent({loading: true})
		const newstate = {loading: false}

		const enterpriseId = getSession().profile.enterprise.id
		const response = await api.post(`/api/enterprises/${enterpriseId}/ai_agents/`, {
			intentions: [agentInitialIntention]
		})

		if (response.status === 201) {
			newstate.agents = [...parent.agents, response.data]
			newstate.agent = response.data
		} else {
			if (response.status === 400 && response.data.detail === "Max AI agents reached.") {
				useNotification(
					"Atenção",
					"Quantidade máxima de agentes atingida.",
					"warning"
				)
			} else
				useNotification(
					"Ops",
					"Não foi possível criar o agente.",
					"danger"
				)
		}

		setParent(newstate)
	}, [parent.agents, parent.agent])

	const CustomToolbar = useCallback(() => (
		<GridToolbarContainer sx={{ display: "flex", gap: 1 }}>
			<Button size="small" onClick={createAgent} startIcon={<AddIcon />}>
				Adicionar
			</Button>
			<Button size="small" onClick={() => setUpload(true)} startIcon={<UploadIcon />}>
				Importar
			</Button>
		</GridToolbarContainer>
	), [parent.agents, parent.agent])

	const duplicateAgent = useCallback(async (id) => {

		setParent({loading: true})
		const newstate = {loading: false}
		const enterpriseId = getSession().profile.enterprise.id
		const response = await api.post(`/api/enterprises/${enterpriseId}/ai_agents/${id}/duplicate/`)

  	if (response.status === 201)
			newstate.agents = [...parent.agents, response.data]
		else if (response.status === 409) {
			useNotification(
					"Atenção",
					"Nome de agente já registrado, favor escolher um nome diferente.",
					"warning"
				)
		}
		else{
			if (response.status === 400 && response.data.detail === "Max AI agents reached.") {
				useNotification(
					"Atenção",
					"Quantidade máxima de agentes atingida.",
					"warning"
				)
			} else
				useNotification(
					"Ops",
					"Não foi possível duplicar o agente.",
					"danger"
				)
		}

		setParent(newstate)

	}, [parent.agents])

	const deleteAgent = useCallback(async (id) => {

		setParent({loading: true})
		const newstate = {loading: false}

		const enterpriseId = getSession().profile.enterprise.id
		const response = await api.delete(`/api/enterprises/${enterpriseId}/ai_agents/${id}/`)
		
		if (response.status === 204) {
			newstate.agents = [...parent.agents.filter((agent) => agent.id !== id)]
			if (parent.agent?.id === id)
				newstate.agent = null
		} else if (response.status === 409){
			setConfirmDuplicate(parent.agent)
			setParent(newstate)
			return
		}
		
		else {
			useNotification(
				"Ops",
				"Não foi possível excluir o agente.",
				"danger"
			)
		}

		setParent(newstate)

	}, [parent.agents, parent.agent])

	const downloadAgent = useCallback(async (agent) => {
		try {
			setParent({ loading: true })
			const enterpriseId = getSession().profile.enterprise.id
			const response = await api.get(`/api/enterprises/${enterpriseId}/ai_agents/${agent.id}/export/`, { responseType: "blob" })
			const url = window.URL.createObjectURL(new Blob([response.data]))
	    const link = document.createElement("a")
	    link.href = url
	    link.setAttribute("download", `${agent.name}.zip`)
	    document.body.appendChild(link)
	    link.click()
	    link.remove()
		} catch (err) {
			console.error(err)
		} finally {
			setParent({ loading: false })
		}
	}, [])

	const columns = useMemo(() => [
    { field: "name", headerName: "Agente", flex: 1 },
    {
      field: "actions",
      headerName: "Ações",
      width: 140,
      renderCell: (params) => (
        <>
        	<Tooltip title={params.row.description}>
	          <IconButton sx={{ p: 0.25 }} size="small">
	            <InfoIcon fontSize="small" />
	          </IconButton>
	        </Tooltip>
          <IconButton sx={{ p: 0.25 }} size="small" onClick={() => setParent({agent: params.row})}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton sx={{ p: 0.25 }} size="small" onClick={() => downloadAgent(params.row)}>
            <DownloadIcon fontSize="small" />
          </IconButton>
          <IconButton sx={{ p: 0.25 }} size="small" onClick={(event) => {
          	event.stopPropagation()
          	duplicateAgent(params.row.id)
          }}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
          <IconButton sx={{ p: 0.25 }} size="small" color="error" onClick={(event) => {
          	event.stopPropagation()
          	setAgentToDelete(params.row)
          }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </>
      ),
    },
  ], [parent.agents, parent.agent])

	useEffect(() => {
		window.openConfirmDuplicate = (d) => setConfirmDuplicate(d)
		return () => {
			window.openConfirmDuplicate = () => {}
		}
	}, [])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    const droppedFile = e.dataTransfer.files[0]
    if (droppedFile) setFile(droppedFile)
  }, [])

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0]
    if (selectedFile) setFile(selectedFile)
  }

  const handleRemoveFile = () => setFile(null)

  const handleUpload = async () => {
	  if (!file) return
	  
	  try {
	  	setParent({ loading: true })
	    
	    const enterpriseId = getSession().profile.enterprise.id
	    const formData = new FormData()
	    formData.append("file", file)
	    
	    const response = await api.post(`/api/enterprises/${enterpriseId}/ai_agents/upload/`, formData, { timeout: 0 })
	    if (response.status === 201) {
		    setFile(null)
		    setUpload(false)
		    loadAgents()
	    }
	    else if (response.data.detail === "Couldn't create knowledge base")
				useNotification(
					"Atenção",
					"Já existe um agente com este nome.",
					"warning"
				)
			else {
				useNotification(
					"Ops",
					"Não foi possível importar o agente.",
					"danger"
				)
			}
	  } catch (error) {
	    console.error("Error uploading file:", error)
	  } finally {
	  	setParent({ loading: false })
	  }
  }

	return (
		<>
			<Box width="20rem" sx={{ cursor: "pointer" }}>
				<ThemeProvider theme={theme}>
				<DataGridPro
					columns={columns}
					rows={parent.agents}
					density="compact"
					components={{ Toolbar: CustomToolbar }}
					onRowClick={(params) => setParent({agent: params.row})}
				/>
				</ThemeProvider>
			</Box>
			{confirmDuplicate && 
			<ConfirmationDialog
					open={true}
					description={
						<>
							<Alert severity="warning">
								O agente selecionado não pode ser modificado/excluído pois está vinculado com algum fluxo.
								Deseja criar uma cópia? 
              				</Alert>
						</>
					}
					title="Copiar Agente de IA"
					handleClose={() => setConfirmDuplicate(null)}
					handleSubmit={() => {
						duplicateAgent(confirmDuplicate.id)
						setConfirmDuplicate(null)
					}}
				/>
			}
			{agentToDelete &&
				<ConfirmationDialogCaptcha
					open={true}
					captcha={"remover"}
					description={
						<>
							<Alert severity="warning">
                Ao remover um Agente de IA, não será mais possível recuperar a base de conhecimento associada.
              				</Alert>
							<Box mt={2}>
								Para remover o agente <span style={{ fontWeight: "bold" }}>{agentToDelete.name}</span>, digite <span style={{ fontWeight: "bold" }}>remover</span> no campo abaixo.
							</Box>
						</>
					}
					alternateYes="Confirmar"
					alternateNo="Fechar"
					title="Remover Agente de IA"
					handleClose={() => setAgentToDelete(null)}
					handleSubmit={() => {
						deleteAgent(agentToDelete.id)
						setAgentToDelete(null)
					}}
				/>
			}
			{upload &&
				<Dialog open={open} onClose={() => setUpload(false)} fullWidth maxWidth="sm">
		      <DialogTitle className="flex justify-between items-center">
		        Importar Agente de IA
		      </DialogTitle>
					<DialogContent>
					  <Box
					    onDrop={handleDrop}
					    onDragOver={(e) => e.preventDefault()}
					    onClick={() => document.getElementById("fileInput2").click()}
					    sx={{
					      border: "2px dashed #ccc",
					      borderRadius: "8px",
					      p: 4,
					      textAlign: "center",
					      cursor: "pointer",
					      transition: "border-color 0.3s",
					      "&:hover": { borderColor: "#1976d2" },
					    }}
					  >
					    {!file ? (
					      <>
					        <Typography variant="body1" sx={{ mb: 1 }}>
					          Arraste e solte aqui, ou clique para escolher o arquivo
					        </Typography>
					      </>
					    ) : (
					      <Box display="flex" justifyContent="center">
						      <Chip
						        label={file.name}
						        onDelete={(e) => {
						          e.stopPropagation()
						          handleRemoveFile()
						        }}
						        sx={{ fontSize: "0.9rem", px: 1 }}
						      />
						    </Box>
					    )}
					  </Box>
					  <input
					    id="fileInput2"
					    type="file"
					    style={{ display: "none" }}
					    onChange={handleFileChange}
					    accept=".zip"
					  />
					</DialogContent>
		      <DialogActions>
		      	<Button onClick={() => setUpload(false)}>
		          Cancelar
		        </Button>
		        <Button variant="contained" onClick={handleUpload} disabled={!file}>
		          Importar
		        </Button>
		      </DialogActions>
		    </Dialog>
			}
		</>
	)
}

const iconColors = {
	unpublished : {color: 'orange', description: 'Base não publicada'},
	publishing  : {color: 'blue', description: 'Base está sendo publicada'},
	published   : {color: 'green', description: 'Base publicada e pronta pra uso'}
}

const ViewAgent = ({parent, setParent}) => {

	const ready = useRef(false)
	const pollingRef = useRef(null)

	const [ fileToRemove, setFileToRemove ] = useState(null)

	const [state, setState] = useReducer(lockedReducer, {
		id: 0,
		persona_id: '',
		name: '',
		persona_identity: '',
		description: '',
		persona_style: '',
		persona_response: '',
		persona_tasks: '',
		intentions: [],
		knowledge_base_id: '',
		files: [],
		edited: "unpublished"
	})

	const validateSave = useMemo(() => {
		const response = []

		if (!state.name || state.name.length === 0)
			response.push(<Box>Adicione um nome ao agente.</Box>)
		if (state.files.length === 0)
			response.push(<Box>Adicione arquivos à base de conhecimento.</Box>)
		if (parent.loading_faq)
			response.push(<Box>Aguarde o fim do processamento do arquivo.</Box>)
		if (parent.publishing || state.edited === 'publishing')
			response.push(<Box>Aguarde o fim da publicação.</Box>)

		if (response.length === 0)
			return ''
		return response	
	}, [state.name, parent.publishing, parent.loading_faq, state.files, state.edited])

	const startKbStatusPolling = useCallback(async (enterpriseId, agent, faq=false) => {
		setParent({ publishing: !faq, loading_faq: faq })
		
		if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }

		pollingRef.current = setInterval(async () => {
      try {
    		setState({ edited: faq ? "unpublished" : "publishing" })
        const check = await api.get(`/api/enterprises/${enterpriseId}/ai_agents/${agent.id}/status/`)
        if (check.status === 200 && check.data === "READY") {
          useNotification(
          	"Sucesso",
          	faq ? "Arquivo processado com sucesso." : "O agente está pronto para uso.",
          	"success"
          )
          if (faq)
          	setParent({ publishing: false, loading_faq: false, generate: true })
        	else
          	setParent({ publishing: false, loading_faq: false })
		  		setState({ edited: faq ? "unpublished" : "published" })
          clearInterval(pollingRef.current)
          pollingRef.current = null
        }
      } catch (err) {
        console.error("Error while polling knowledge base status", err)
        clearInterval(pollingRef.current)
        pollingRef.current = null
      }
		}, 5000)
	}, [])

	useEffect(() => {
		ready.current = false
		setParent({ generate: false })
		const initialize = async () => {
			if (parent.agent.id) {
				const newstate = parent.agent
				setParent({loading: true, publishing: false})
				const enterpriseId = getSession().profile.enterprise.id
				const response = await api.get(`/api/enterprises/${enterpriseId}/ai_agents/${parent.agent.id}/files/`)
				if (response.status === 200){
					const knowledgeBase = response.data.KnowledgeBase
					newstate.files = (knowledgeBase?.files || []) 
					newstate.edited = knowledgeBase?.publishing
						? "publishing"
						: knowledgeBase?.versions?.edited_ver === false
							? "published"
							: "unpublished" 
					setParent({ generate: newstate.edited === "unpublished" })
				}
				else {
				useNotification(
					"Ops",
					"Não foi possível carregar o agente.",
					"danger"
				)
				}
				setState(newstate)
				ready.current = true
				setParent({loading: false})
			}
		}
		initialize()
	}, [parent.agent])

	useEffect(() => {
		const checkKbStatus = async () => {
			const enterpriseId = getSession().profile.enterprise.id
	    const check = await api.get(`/api/enterprises/${enterpriseId}/ai_agents/${parent.agent.id}/status/`)
			if (check.status === 200 && !["EMPTY", "READY"].some((e) => check.data.includes(e))){
				startKbStatusPolling(enterpriseId, parent.agent, check.data === "LOADING")
			}
		}
		checkKbStatus()
		
		return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
        pollingRef.current = null
      }
    }
	}, [parent.agent])

	const [expandedAccordions, setExpandedAccordions] = useState({
		identity           : true,
		description        : false,
		styleDirectives    : false,
		responseDirectives : false,
		tasks              : false,
		intentions         : false,
		knowledgeBaseFiles : false,
	})

	const createKnowledgeBase = useCallback(async (agent) => {
		const newstate = {agent: {...agent}, agents: parent.agents}
		const enterpriseId = getSession().profile.enterprise.id
		const response = await api.post(`/api/enterprises/${enterpriseId}/ai_agents/${agent.id}/create_knowledge_base/`, {
			"name": agent.name
		})
		
		if (response.status === 201) {
			const knowledgeBaseId = response.data.KnowledgeBase?.id ?? null
			newstate.agent.knowledge_base_id = knowledgeBaseId
			agent.knowledge_base_id = knowledgeBaseId
			const parent_agent = parent.agents.find(a => (a.id === agent.id))
			if (parent_agent)
				parent_agent.knowledge_base_id = knowledgeBaseId
			newstate.agents = [...parent.agents]
		} else {
			useNotification(
				"Ops",
				"Não foi possível criar a base de conhecimento.",
				"danger"
			)
		}
	}, [parent.agents])

	const uploadFile = useCallback(async (file, agent) => {

		setParent({loading: true})

		if (!agent.knowledge_base_id)
			await createKnowledgeBase(agent)

		const enterpriseId = getSession().profile.enterprise.id
		const formData = new FormData()
		formData.append("file", file)
		formData.append("knowledge_base_id", agent.knowledge_base_id)
	    const response = await api.post(`/api/enterprises/${enterpriseId}/ai_agents/${agent.id}/upload_file/`, formData, { timeout: 0 })
	    if (response.status === 201) {
	    	startKbStatusPolling(enterpriseId, agent, true)
				setState({ files: [...state.files, ...response.data.UploadedFiles] })
	    }
		else {
			useNotification(
				"Ops",
				"Não foi possível carregar o arquivo.",
				"danger"
			)
		}

		setParent({loading: false})
	}, [state.files])

	const removeFile = useCallback(async (id, agent) => {

		setParent({loading: true})

		const enterpriseId = getSession().profile.enterprise.id
	    const response = await api.post(`/api/enterprises/${enterpriseId}/ai_agents/${agent.id}/remove_file/`, {
	    	"file_id": id,
	    	"knowledge_base_id": agent.knowledge_base_id
	    })
		if (response.status === 204) {
			setState({files: [...state.files.filter(file => file.id !== id)], edited: 'unpublished'})
			setParent({ generate: true })
		}
		else {
			useNotification(
				"Ops",
				"Não foi possível remover o arquivo.",
				"danger"
			)
		}
		setParent({loading: false})
	}, [state])	

	const handleSave = useCallback(async (data, files) => {

		setParent({loading: true})
		const newstate = {loading: false}

		const enterpriseId = getSession().profile.enterprise.id
		
			const response = await api.patch(`/api/enterprises/${enterpriseId}/ai_agents/${data.id}/`, {
				...data,
				generate: parent.generate
			})
			
			if (response.status === 200) {
				const updatedAgent = response.data
				newstate.agents = parent.agents.map((agent) =>
					agent.id === updatedAgent.id ? updatedAgent : agent
				)
				newstate.agent = updatedAgent
				if (parent.generate)
					startKbStatusPolling(enterpriseId, data)
				else
					useNotification(
						"Sucesso",
						"Agente salvo com sucesso.",
						"success"
					)
			} 
			else if (response.status === 409) {
				openConfirmDuplicate(data)
				setParent(newstate)
				return
			}			
			else {
				if (response.status === 400 && response.data.detail === "Agent with that name already exists.")
					useNotification(
						"Atenção",
						"Já existe um agente com este nome.",
						"warning"
					)
				else
					useNotification(
						"Ops",
						"Não foi possível salvar o agente.",
						"danger"
					)
			}
		setParent(newstate)
	}, [parent.agents, parent.generate])

	const handleDownload = useCallback(async (file) => {
	  try {
	    const enterpriseId = getSession().profile?.enterprise?.id
	    const response = await api.get(
	      `/api/enterprises/${enterpriseId}/ai_agents/download/?knowledgeBaseId=${state.knowledge_base_id}&fileName=${file.name}`,
	      { responseType: "blob" }
	    )

	    const extension = file.name.split('.').pop().toLowerCase()

	    const mimeTypes = {
	      pdf: "application/pdf",
	      txt: "text/plain",
	      doc: "application/msword",
	      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
	    }

	    const mimeType = mimeTypes[extension] || "application/octet-stream"

	    const blob = new Blob([response.data], { type: mimeType })
	    const url = window.URL.createObjectURL(blob)

	    if (extension === "pdf") {
	      window.open(url, "_blank")
	    } else {
	      const link = document.createElement("a")
	      link.href = url
	      link.download = file.name
	      document.body.appendChild(link)
	      link.click()
	      document.body.removeChild(link)
	    }

	    setTimeout(() => window.URL.revokeObjectURL(url), 10000)
	  } catch (err) {
	    console.error("Error downloading file:", err)
	  }
	}, [state])

	return (
		<>
			<Grid item xs={12} md={8} display="flex" flexDirection="column" gap={2} height="100%">
				<Box flex={1} overflow="auto">
					{useMemo(()=><Accordion
					expanded={expandedAccordions.identity}
					onChange={() =>
						setExpandedAccordions(prev => ({
							...prev,
							identity: !prev.identity,
						}))
						}
					disableGutters
					elevation={0}
					square
					sx={{
						border: "1px solid #ddd",
						"&::before": { display: "none" },
						borderRadius: 1,
						overflow: "hidden",
						mb: 2
						}}
					>
					<AccordionSummary expandIcon={<ExpandMoreIcon />}>
						<Typography display="flex" gap={1}>
						<AccountCircleIcon sx={{ color: "gray" }}/>
						Identidade
						</Typography>
					</AccordionSummary>
					<AccordionDetails sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
						<Box className='counter-wrapper'>
							<TextField
								label="Nome"
								fullWidth
								size="small"
								value={state.name}
								onChange={(event) => setState({ name: event.target.value })}
								inputProps={{
									maxLength: limits.name
								}}
							/>
							<Box className="character-counter positionUnset" >
										{`${state.name.length} / ${limits.name} caracteres`}
							</Box>
						</Box>
						<Box className='counter-wrapper'>
							<TextField
								label="Descrição"
								placeholder="Você é um assistente virtual criado para ajudar usuários com dúvidas gerais, orientações e suporte básico."
								multiline
								fullWidth
								minRows={4}
								value={state.persona_identity ?? ""}
								onChange={(event) => setState({  persona_identity: event.target.value })}
								inputProps={{
										maxLength: limits.persona_identity
									}}
							/>
							<Box className="character-counter positionUnset" >
										{`${(state.persona_identity || '').length} / ${limits.persona_identity} caracteres`}
							</Box>
						</Box>
					</AccordionDetails>
					</Accordion>, [expandedAccordions.identity, state.name, state.persona_identity])}

					{useMemo(()=><Accordion
					expanded={expandedAccordions.description}
					onChange={() =>
						setExpandedAccordions(prev => ({
							...prev,
							description: !prev.description,
						}))
						}
					disableGutters
					elevation={0}
					square
					sx={{
						border: "1px solid #ddd",
						"&::before": { display: "none" },
						borderRadius: 1,
						overflow: "hidden",
						mb: 2
						}}
					>
					<AccordionSummary expandIcon={<ExpandMoreIcon />}>
						<Typography display="flex" gap={1}>
						<InfoIcon sx={{ color: "gray" }}/>
						Descrição
						</Typography>
					</AccordionSummary>
					<AccordionDetails sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
						<Box className='counter-wrapper'>
							<TextField
								label="Descrição"
								placeholder="Descrição livre, nada descrito aqui será utilizado para controle do agente de IA."
								multiline
								fullWidth
								minRows={4}
								value={state.description ?? ""}
								onChange={(event) => setState({  description: event.target.value })}
								inputProps={{
										maxLength: limits.description
									}}
							/>
							<Box className="character-counter positionUnset" >
										{`${(state.description || '').length} / ${limits.description} caracteres`}
							</Box>
						</Box>
					</AccordionDetails>
					</Accordion>, [expandedAccordions.description, state.description])}

					{useMemo(()=><Accordion
					expanded={expandedAccordions.knowledgeBaseFiles}
					onChange={() =>
						setExpandedAccordions(prev => ({
							...prev,
							knowledgeBaseFiles: !prev.knowledgeBaseFiles,
						}))
						}
					disableGutters
					elevation={0}
					square
					sx={{
						border: "1px solid #ddd",
						"&::before": { display: "none" },
						borderRadius: 1,
						overflow: "hidden",
						mb: 2
					}}
					>
					<AccordionSummary expandIcon={<ExpandMoreIcon />}>
						<Typography display="flex" gap={1}>
						<LibraryBooksIcon sx={{ color: "gray" }}/>
						Base de Conhecimento
						</Typography>
					</AccordionSummary>
					<AccordionDetails>
						<Box mb={2}>
							<Alert severity="info">
								<AlertTitle>
									Como enviar arquivos .xls ou .xlsx?
								</AlertTitle>
								<Box mt={2}>
									O arquivo deve conter obrigatoriamente as seguintes colunas:
									<ul style={{ marginTop: 8, marginBottom: 8 }}>
										<li><strong>Pergunta</strong></li>
										<li><strong>Resposta</strong></li>
									</ul>
								</Box>
								<Box mb={2}>
									Opcionalmente, você pode incluir uma coluna <strong>Tags</strong>, contendo palavras-chave separadas por vírgula.
								</Box>
								<a href="/exemplo.xlsx">Clique aqui</a> para baixar um arquivo de exemplo.
							</Alert>
						</Box>
						<Box
							disabled={parent.publishing || parent.loading_faq}
							onDrop={(event) => {
								event.preventDefault()
								const newFiles = Array.from(event.dataTransfer.files)
								if (newFiles.length > 0) {
									const lastFile = newFiles[newFiles.length-1]
									const fileExtension = lastFile.name.toLowerCase().slice(lastFile.name.lastIndexOf("."))
									if (!allowedExtensions.includes(fileExtension)) {
										useNotification(
											"Atenção",
											"Formato de arquivo não permitido. Use .pdf, .txt, .doc, .docx, .md, .xlsx ou .xls.",
											"warning"
										)
										return
									}
									uploadFile(lastFile, state)
								}
							}}
							onDragOver={(event) => event.preventDefault()}
							onClick={() => document.getElementById("fileInput").click()}
							sx={{
								border: "1px solid #ccc",
								borderRadius: 1,
								p: 2,
								textAlign: "center",
								color: "#666",
								cursor: "pointer",
								"&:hover": { backgroundColor: "#fafafa" },
							}}
						>
						<Typography variant="body2" sx={{ mb: 1 }}>
							{parent.publishing 
								? "Aguarde o fim da publicação para modificar..."
								: parent.loading_faq
									? "Aguarde o fim do processamento do arquivo..."
									: "Arraste e solte arquivos aqui ou clique para enviar"
								}
						</Typography>
						<input
							disabled={parent.publishing || parent.loading_faq}
							id="fileInput"
							type="file"
							multiple
							hidden
							accept={allowedExtensions.join(",")}
							onChange={(event) => {
								const newFiles = Array.from(event.target.files)
								if (newFiles.length > 0) {
									const lastFile = newFiles[newFiles.length-1]
									const fileExtension = lastFile.name.toLowerCase().slice(lastFile.name.lastIndexOf("."))
									if (!allowedExtensions.includes(fileExtension)) {
									  useNotification(
											"Atenção",
											"Formato de arquivo não permitido. Use .pdf, .txt, .doc, .docx, .md, .xlsx ou .xls.",
											"warning"
										)
									  event.target.value = ""
									  return
									}
									uploadFile(lastFile, state)
								}
								event.target.value = ""
							}}
						/>
						{state.files.length > 0 && (
							<Box display="flex" flexWrap="wrap" gap={1} justifyContent="center" mt={1}>
							{state.files.map((file, index) => (
								<Chip
									key={index}
									label={file.name}
									disabled={parent.publishing || parent.loading_faq}
									onDelete={(event) => {
										event.stopPropagation()
										setFileToRemove(file)
									}}
									deleteIcon={
									  <Box display="flex" alignItems="center">
									    <IconButton
									      size="small"
									      onClick={(event) => {
									        event.stopPropagation()
									        handleDownload(file)
									      }}
									      sx={{ padding: ".25rem" }}
									    >
									      <DownloadIcon fontSize="1rem" />
									    </IconButton>
									    <IconButton 
									      size="small"
									      sx={{ padding: ".25rem" }}
									    >
									    	<CloseIcon fontSize="1rem" />
									    </IconButton>
									  </Box>
									}
								/>
							))}
							</Box>
						)}
					</Box>
				</AccordionDetails>
					</Accordion>, [expandedAccordions.knowledgeBaseFiles, state])}

					{useMemo(()=><Accordion
					expanded={expandedAccordions.styleDirectives}
					onChange={() =>
						setExpandedAccordions(prev => ({
							...prev,
							styleDirectives: !prev.styleDirectives,
						}))
					}
					disableGutters
					elevation={0}
					square
					sx={{
						border: "1px solid #ddd",
						"&::before": { display: "none" },
						borderRadius: 1,
						overflow: "hidden",
						mb: 2
					}}
					>
					<AccordionSummary expandIcon={<ExpandMoreIcon />}>
						<Typography display="flex" gap={1}>
						<PaletteIcon sx={{ color: "gray" }}/>
						Diretrizes de Estilo
						</Typography>
					</AccordionSummary>
					<AccordionDetails>
						<Box className='counter-wrapper'>
							<TextField
								label="Descrição"
								placeholder={`Seja educado, claro e objetivo.
	Use linguagem simples e acessível.
	Evite jargões técnicos desnecessários.
	Seja prestativo e ofereça ajuda adicional quando possível.`}
								multiline
								fullWidth
								minRows={4}
								value={state.persona_style ?? ""}
								onChange={(event) => setState({  persona_style: event.target.value })}
								inputProps={{
											maxLength: limits.persona_style
										}}
							/>
							<Box className="character-counter positionUnset" >
										{`${(state.persona_style || '').length} / ${limits.persona_style} caracteres`}
							</Box>
						</Box>
					</AccordionDetails>
					</Accordion>, [expandedAccordions.styleDirectives, state.persona_style])}

					{useMemo(()=><Accordion
					expanded={expandedAccordions.responseDirectives}
					onChange={() =>
						setExpandedAccordions(prev => ({
							...prev,
							responseDirectives: !prev.responseDirectives,
						}))
					}
					disableGutters
					elevation={0}
					square
					sx={{
						border: "1px solid #ddd",
						"&::before": { display: "none" },
						borderRadius: 1,
						overflow: "hidden",
						mb: 2
						}}
					>
					<AccordionSummary expandIcon={<ExpandMoreIcon />}>
						<Typography display="flex" gap={1}>
						<ChatIcon sx={{ color: "gray" }}/>
						Diretrizes de Resposta
						</Typography>
					</AccordionSummary>
					<AccordionDetails>
						<Box className='counter-wrapper'>
							<TextField
								label="Descrição"
								placeholder={`Não repita a pergunta do usuário.
	Se não souber algo, diga que irá verificar ou direcionar para o time responsável.
	Não invente informações.
	Mantenha sempre um tom amistoso e profissional.`}
								multiline
								fullWidth
								minRows={4}
								value={state.persona_response ?? ""}
								onChange={(event) => setState({persona_response: event.target.value })}
								inputProps={{
											maxLength: limits.persona_response
										}}
							/>
							<Box className="character-counter positionUnset" >
										{`${(state.persona_response || '').length} / ${limits.persona_response} caracteres`}
							</Box>
						</Box>
					</AccordionDetails>
					</Accordion>, [expandedAccordions.responseDirectives, state.persona_response])}

					{useMemo(()=><Accordion
					expanded={expandedAccordions.tasks}
					onChange={() =>
						setExpandedAccordions(prev => ({
							...prev,
							tasks: !prev.tasks,
						}))
					}
					disableGutters
					elevation={0}
					square
					sx={{
						border: "1px solid #ddd",
						"&::before": { display: "none" },
						borderRadius: 1,
						overflow: "hidden",
						mb: 2
						}}
					>
					<AccordionSummary expandIcon={<ExpandMoreIcon />}>
						<Typography display="flex" gap={1}>
						<AssignmentIcon sx={{ color: "gray" }}/>
						Tarefas
						</Typography>
					</AccordionSummary>
					<AccordionDetails>
						<Box className='counter-wrapper'>
							<TextField
							label="Descrição"
							placeholder={`Cumprimente o usuário de forma simpática.
	Responda dúvidas com base nas informações disponíveis.
	Ofereça ajuda complementar, se apropriado.
	Indique um canal humano de suporte, quando necessário.`}
							multiline
							fullWidth
							minRows={4}
							value={state.persona_tasks ?? ""}
							onChange={(event) => setState({ persona_tasks: event.target.value })}
							inputProps={{
										maxLength: limits.persona_tasks
										}}
							/>
							<Box className="character-counter positionUnset" >
										{`${(state.persona_tasks || '').length} / ${limits.persona_tasks} caracteres`}
							</Box>
						</Box>
					</AccordionDetails>
					</Accordion>, [expandedAccordions.tasks, state.persona_tasks])}

					{useMemo(()=><Accordion
						expanded={expandedAccordions.intentions}
						onChange={() =>
							setExpandedAccordions((prev) => ({
								...prev,
								intentions: !prev.intentions,
							}))
						}
						disableGutters
						elevation={0}
						square
						sx={{
							border: "1px solid #ddd",
							"&::before": { display: "none" },
							borderRadius: 1,
							overflow: "hidden",
							mb: 2,
						}}
					>
									<AccordionSummary expandIcon={<ExpandMoreIcon />}>
									<Typography display="flex" gap={1}>
										<PsychologyIcon sx={{ color: "gray" }} />
										Intenções
									</Typography>
									</AccordionSummary>
									<AccordionDetails>
									<Box display="flex" flexDirection="column" gap={2}>
										{state.intentions && state.intentions.map((intention, index) => (
										<Box key={index}>
											<Grid container spacing={2} alignItems="center">
											<Grid item xs={12}>
												<Box className='counter-wrapper'>
												<TextField
												label="Descrição"
												fullWidth
												size="small"
												multiline
												rows={3}
												placeholder="Descreva em que condições o atendimento deve ser transferido..."
												value={intention.description || ''}
												onChange={(e) => {
													const updated = [...state.intentions]
													updated[index] = { ...updated[index], description: e.target.value }
													setState({ intentions: updated })
												}}
												inputProps={{
													maxLength: limits.intention_description
												}}
												/>
												<Box className="character-counter positionUnset" >
													{`${(intention.description || '').length} / ${limits.intention_description} caracteres`}
												</Box>
												</Box>
											</Grid>
											<Grid item xs={12}>
												<Box className='counter-wrapper'>
													<TextField
													label="Mensagem a ser enviada"
													fullWidth
													size="small"
													placeholder="Mensagem enviada ao executar ação."
													value={intention.message || ''}
													onChange={(e) => {
														const updated = [...state.intentions]
														updated[index] = { ...updated[index], message: e.target.value }
														setState( { intentions: updated })
													}}
													inputProps={{
														maxLength: limits.intention_description
													}}
													/>
													<Box className="character-counter positionUnset" >
														{`${(intention.message || '').length} / ${limits.intention_description} caracteres`}
													</Box>
												</Box>
											</Grid>
											<Grid item xs={12} display="flex" gap={1}>
												<FormControl fullWidth size="small">
												<InputLabel id={`select-type-label-${index}`}>Tipo</InputLabel>
												<Select
													labelId={`select-type-label-${index}`}
													label="Tipo"
													value={intention.type}
													onChange={(e) => {
													const updated = [...state.intentions]
													updated[index] = { ...updated[index], type: e.target.value }
													setState({ intentions: updated })
													}}
												>
													{Object.entries(functions).map(([key, value]) => (
														<MenuItem value={key}>{value}</MenuItem>
													))}
												</Select>
												</FormControl>

												{intention.type !== "#FINALIZAR_ATENDIMENTO" &&
													<FormControl fullWidth size="small">
														<InputLabel id={`select-value-label-${index}`}>
															{intention.type === "#TRANSFERIR_BOT" ? "ChatBot" : intention.type === "#TRANSFERIR_FILA" ? "Fila" : "Agente de IA"}
														</InputLabel>
														<Select
															labelId={`select-value-label-${index}`}
															label={intention.type === "#TRANSFERIR_BOT" ? "ChatBot" : intention.type === "#TRANSFERIR_FILA" ? "Fila" : "Agente de IA"}
															value={intention.value}
															onChange={(e) => {
															const updated = [...state.intentions]
															updated[index] = { ...updated[index], value: e.target.value }
															setState({ intentions: updated })
															}}
														>
															{intention.type === "#TRANSFERIR_BOT" && 
																(parent.bots).map(item => (
																	<MenuItem value={item.id}>{item.label}</MenuItem>
																))
															}
															{intention.type === "#TRANSFERIR_FILA" && 
																(parent.queues).map(item => (
																	<MenuItem value={item.id}>{item.label}</MenuItem>
																))
															}
															{intention.type === "#TRANSFERIR_AGENTE" && 
																(parent.agents).filter(item => parent.agent.id !== item.id).map(item => (
																	<MenuItem value={item.id}>{item.name}</MenuItem>
																))
															}
														</Select>
													</FormControl>
												}

												<IconButton
													disabled={intention.required}
													color="error"
													onClick={() => {
													const updated = state.intentions.filter((i) => i !== intention)
													setState({ intentions: updated })
													}}
													size="small"
												>
													<DeleteIcon fontSize="small" />
												</IconButton>
												
											</Grid>
											</Grid>

											{index !== state.intentions.length - 1 && <Divider sx={{ mt: 2 }} />}
										</Box>
										))}

										<Box display="flex" justifyContent="flex-end">
										<Button
											disabled={state.intentions.length > limits.intentions}
											variant="contained"
											color="warning"
											startIcon={<AddIcon />}
											onClick={() =>
											setState({
												intentions: [
												...state.intentions,
												{ description: "", value: "", type: "" },
												],
											})
											}
										>
											Adicionar
										</Button>
										</Box>
									</Box>
									</AccordionDetails>
					</Accordion>, [expandedAccordions.intentions, state.intentions])}

				</Box>
				<Box display="flex" gap={2} sx={{alignItems: 'center'}}>
					<Tooltip title={validateSave}>
						<span>
							<Button
								variant="contained"
								color="primary"
								startIcon={<SaveIcon />}
								sx={{ alignSelf: "flex-end" }}
								onClick={() => handleSave(state, state.files)}
								disabled={validateSave}
							>
								Salvar
							</Button>
						</span>
					</Tooltip>
					<Tooltip title={iconColors[state.edited]?.description}>
						<CircleIcon 
							sx={{color: iconColors[state.edited]?.color}}
						/>
					</Tooltip>
					<Button
						size="small"
						startIcon={Object.values(expandedAccordions).every(v => v) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
						onClick={() => {
							const isAnyOpen = Object.values(expandedAccordions).every(v => v)
							const newState = Object.fromEntries(
								Object.keys(expandedAccordions).map(key => [key, !isAnyOpen])
							)
							setExpandedAccordions(newState)
						}}
					>
						{Object.values(expandedAccordions).every(v => v) ? "Fechar todos" : "Abrir todos"}
					</Button>
				</Box>
				{fileToRemove &&
					<ConfirmationDialogCaptcha
						open={true}
						captcha={"remover"}
						description={
							<Box>
								Para remover o arquivo <span style={{ fontWeight: "bold" }}>{fileToRemove.name}</span>, digite <span style={{ fontWeight: "bold" }}>remover</span> no campo abaixo.
							</Box>
						}
						alternateYes="Confirmar"
						alternateNo="Fechar"
						title="Remover Agente de IA"
						handleClose={() => setFileToRemove(null)}
						handleSubmit={() => {
							removeFile(fileToRemove.id, state)
							setFileToRemove(null)
						}}
					/>
				}
			</Grid>
		</>
	)
}

let closing = false

const TestAgent = ({parent, setParent}) => {

	const [state, setState] = useReducer(lockedReducer, {
		question: "",
		messages: [],
		conversationId: null,
		thinking: false,
	})

	const closeConversation = async () => {
	if (!state.conversationId) return
	if (closing === true) return
		closing = true
		const enterpriseId = getSession().profile.enterprise.id
		await api.post(`/api/enterprises/${enterpriseId}/ai_agents/${parent.agent.id}/close_conversation/`, {
			conversation_id: state.conversationId
		})
		closing = false
	}

	useEffect(() => {
		if (!parent.agent) return

		setState({
			messages: [],
			conversationId: null
		})
	}, [parent.agent])

	useEffect(()=>{

		if (!parent.agent) return
		if (!state.conversationId) return
		let closing = false
		return () => {
			closeConversation()
		}
	}, [state.conversationId, parent.agent])

	const askQuestion = useCallback(async (question, agent) => {
  	if (!question || !agent.knowledge_base_id)
  		return

	const user = getSession()
	const username = user.username
	const accountcode = user.profile.enterprise.accountcode
  	const externalId = state.conversationId || `kwik:${accountcode}:${username}:${dayjs().unix()}`

	const messages = state.messages
	messages.push({text: question, type: "QUESTION"})

	setState({
		messages: [...messages ],
		question: "",
		conversationId: externalId,
		thinking: true
	})

  	const enterpriseId = getSession().profile.enterprise.id
  	const response = await api.post(`/api/enterprises/${enterpriseId}/ai_agents/${agent.id}/ask_question/`, {
  		question: question,
			knowledge_base_id: agent.knowledge_base_id,
			external_id: externalId
  	}, { timeout: 0 })

	const newstate = {thinking: false}

  	// TODO: add visual effect while cortex is "thinking"
  	if (response.status === 200) {
		messages.push( {text: response.data.response?.answer, type: "ANSWER"})
		newstate.messages = [...messages]
		newstate.question = ""
		} else {
			useNotification(
				"Ops",
				"Não foi possível perguntar ao agente.",
				"danger"
			)
		}
  	
	setState(newstate)

  }, [state.messages, state.conversationId])

	return (
		<>
			<Grid item xs={12} md={4} height="100%">
				<Box
				sx={{
					border: "1px solid #ddd",
					borderRadius: 1,
					p: 2,
					height: "100%",
					display: "flex",
					flexDirection: "column",
				}}
				>
				{state.messages.length === 0 &&
					<Box flexGrow={1} display="flex" alignItems="center" justifyContent="center" mb={2}>
						<Typography variant="body2" color="text.secondary" p={4} textAlign="center">
						Digite uma mensagem abaixo para iniciar os testes do seu agente.
						</Typography>
					</Box>
				}

				{state.messages.length !== 0 &&
					<Box display="flex" flexDirection="column-reverse" gap={1} flexGrow={1} mb={2} overflow="auto">
						{[...state.messages].reverse().map((message, index) => (
							<Box
										key={index}
										sx={{
											alignSelf: message.type === "QUESTION" ? "flex-end" : "flex-start",
											backgroundColor: message.type === "QUESTION" ? "#5399db" : "#21446c",
											color: "white",
											borderRadius: 1,
											p: 1,
											maxWidth: "90%",
											wordBreak: "break-word",
											lineHeight: "1.25rem"
										}}
										>
								{message.text}
							</Box>
						))}
					</Box>
				}

				<Paper component="form" sx={{ display: "flex", alignItems: "center", p: "2px 4px" }}>
					<InputBase
						disabled={!parent.agent.knowledge_base_id || parent.publishing || parent.loading_faq || state.thinking}
						sx={{ ml: 1, flex: 1 }}
						placeholder={state.thinking ? "O Agente está digitando" : "Digite sua mensagem"}
						value={state.question}
						onChange={(event) => setState({question: event.target.value})}
						onKeyDown={(event) => {
							if (event.key === "Enter" && !event.shiftKey) {
								event.preventDefault()
								askQuestion(state.question, parent.agent)
							}
						}}
					/>
					<IconButton
						color="primary"
						onClick={() => askQuestion(state.question, parent.agent)}
						disabled={!state.question || !parent.agent.knowledge_base_id || parent.publishing || parent.loading_faq || state.thinking}
					>
						<SendIcon />
					</IconButton>
				</Paper>
				</Box>
			</Grid>
		</>
	)
}


const Cortex = () => {
	const { configurableUrls } = useMenu()

	const { activeTab } = useContext(constructorContext)
	const [state, setState] = useReducer(lockedReducer, {
		agents: [],
		agent: null,
		files: [],
		bots: [],
		queues: [],
		loading: false,
		publishing: false,
		loading_faq: false,
		generate: false
	})
	
	const loadAgents = async	() => {
		const enterpriseId = getSession().profile.enterprise.id
		
		setState({loading: true})
		const [agentResponse, builderResponse, queuesResponse] = await Promise.all([
			api.get(`/api/enterprises/${enterpriseId}/ai_agents/`),
			api.get('/api/getBuilder/'),
			api.get(`/api/enterprises/${enterpriseId}/queues/chat/`)
		])

		const newstate = {loading: false}

		if (agentResponse.status === 200) {
			const allAgents = agentResponse.data
			allAgents.map(agent => {
				if ((agent.intentions || []).length === 0)
					agent.intentions = [agentInitialIntention]
			})
			newstate.agents = allAgents
			if (allAgents.length > 0) 
				newstate.agent = allAgents[0]
		} else {
			useNotification(
				"Ops",
				"Não foi possível carregar os agentes.",
				"danger"
			)
		}

		if (builderResponse.status === 200) {
			newstate.bots = builderResponse.data.botlist.flatMap(bot => {
				if (!bot.publicId) return []
				return ({id: bot.id, label: bot.name})
			}) || []
		} else {
			useNotification(
				"Ops",
				"Não foi possível carregar os bots.",
				"danger"
			)
		}

		if (queuesResponse.status === 200) {
			newstate.queues = queuesResponse.data.map(queue => ({id: queue.name, label: queue.description})) || []
		} else {
			useNotification(
				"Ops",
				"Não foi possível carregar as filas.",
				"danger"
			)
		}

		setState(newstate)
	}
		
	useEffect(() => {
		loadAgents()
	}, [])

  return (
  	<>
			<Loading loading={state.loading} />
			{getSession().profile?.enterprise?.has_assistant && getSession().profile?.enterprise?.max_ai_agents > 0
				? activeTab === 0
		  		? getSession().profile?.has_assistant
		  			? <Box height="100%" display="flex" gap={2} bgcolor="white" position="relative">
				      <AgentList parent={state} setParent={setState} loadAgents={loadAgents} />
				      <Box display="flex" flex={1} gap={2}>
				        {state.agent && (
				          <Grid container spacing={2}>
							<ViewAgent parent={state} setParent={setState} />
							<TestAgent parent={state} setParent={setState} />
				          </Grid>
				        )}
				      </Box>
				      {(state.publishing || state.loading_faq) &&
				      	<Box className="publishing-box">
						      <CircularProgress size={20} color="inherit" />
						      <Typography variant="body2">
						      	{state.loading_faq && "O arquivo está sendo processado."}
						        {state.publishing && "O agente está sendo preparado."}
						      </Typography>
						    </Box>
				    	}
				    </Box>
				    : <ContentUnavailable
		          icon={<SupportAgentIcon />}
		          title="Recurso indisponível para seu usuário."
		          description={
		            <span>
                    Entre em contato com seu administrador para ativar.
                </span>
		          }
		        />
		  		: <></>
		  	: <ContentUnavailable
          icon={<SupportAgentIcon />}
          title="Recurso indisponível para sua conta."
          description={
            <a href={configurableUrls?.agentes_de_ia ? configurableUrls.agentes_de_ia : "https://solucoes.instant.com.br/kwik/#agentes_de_ia"} target="_blank" rel="noopener noreferrer">
                Para mais informações sobre nossos Agentes de IA, clique aqui.
            </a>
          }
        />
			}
  	</>
  )
}

export default Cortex
